import { Fragment, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { BackArrowIcon, Button, ExportIcon, InputLabel, LoadingSpinner, ReportIcon, TextBox, TextInput } from '@cfs-fixings/shared'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/stores'
import ProfileDropdown from '@/components/header/ProfileDropdown'
import { useGetAuthSessionQuery } from '@/api/serverApi'
import { useDebouncedCallback } from 'use-debounce'
import { generateReportAction } from '@/stores/report-preview'

// Configure React PDF
import { Document, Page, pdfjs } from 'react-pdf'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

type PropTypes = {
  isOpen: boolean
  onClose?: () => void
  designId: string
}

const SideBarEntry = ({ title, value, hasTopBorder = true }: { title: string, value: string, hasTopBorder?: boolean }) => (
  <div className={classNames('pt-5 px-5 flex flex-col gap-[7px] border-t border-gray-100', { 'border-none pt-0': !hasTopBorder })}>
    <p className="font-mono text-xs font-semibold uppercase text-gray-550">{title}</p>
    <p className="text-xs font-semibold text-gray-900">{value}</p>
  </div>
)

export default function PreviewReportOverlay ({ isOpen = false, onClose, designId }: PropTypes) {
  const [pageCount, setPageCount] = useState<number | undefined | null>(null)

  const reportUrl = useSelector((state: RootState) => state.reportPreviewSlice.reportUrl)
  const generatingReport = useSelector((state: RootState) => state.reportPreviewSlice.generatingReport)
  const activeDesign = useSelector((state: RootState) => state.activeDesign.design)

  const { data: user } = useGetAuthSessionQuery()

  const dispatch = useDispatch<AppDispatch>()

  const [revisionName, setRevisionName] = useState<string | null>(null)
  const [designNotes, setDesignNotes] = useState<string | null>(null)

  const onDetailsChange = useDebouncedCallback(() => {
    dispatch(generateReportAction({
      details: {
        revision_name: revisionName,
        design_notes: designNotes,
      },
    }))
  }, 1000)

  const handleExport = () => {
    const url = reportUrl!

    const link = document.createElement('a')

    link.target = '_blank'
    link.href = url
    link.download = `${designId}-report.pdf`

    link.click()
    link.remove()
  }

  const handleClose = () => {
    setRevisionName(null)
    setDesignNotes(null)
    onClose?.()
  }

  const pages = useMemo(() => {
    const pages = []
    for (let i = 1; i <= (pageCount ?? 0); i++) {
      pages.push(
        <Page
          key={`report-page-${i}`}
          pageNumber={i}
          className="border border-gray-100 my-[15px]"
          // Loading component is empty because we're using a full-page LoadingSpinner
          loading={<></>}
        />,
      )
    }

    return pages
  }, [pageCount])

  return (
    <Transition.Root
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        as="div"
        className="relative z-10"
        onClose={handleClose}
      >
        <div className="fixed inset-0 z-10 w-full h-full">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="relative h-full transition-all transform bg-white">
              <div className="flex flex-col h-full">
                <nav className="flex items-center h-[65px] bg-white text-black border-b border-gray-100">
                  <div className="flex justify-between items-center w-full h-full pt-[33px] pl-[23px] pr-[13px] pb-[17px]">
                    <button
                      className="font-mono font-semibold text-[13px] uppercase flex flex-row gap-1.5 items-center"
                      onClick={handleClose}
                    >
                      <BackArrowIcon />
                      Back to design
                    </button>
                    <p className="font-medium text-[14px]">
                      {activeDesign?.name}
                    </p>
                    <ProfileDropdown user={user?.data} size="small" />
                  </div>
                </nav>
                <div
                  className={classNames('w-full flex flex-row min-h-0', {
                    // Not the cleanest, but the background colour stops partway down the page if
                    // this is set when data is loaded
                    'h-full': (!reportUrl && !pageCount) || generatingReport,
                  })}
                >
                  <div className="w-[360px] bg-white border-r border-gray-100 flex flex-col gap-5 divide-gray-100">
                    <div className="flex flex-row gap-[5px] px-5 pt-5">
                      <ReportIcon className="text-gray-900" />
                      <p className="font-mono uppercase text-[13px] font-semibold">Report Details</p>
                    </div>

                    <SideBarEntry
                      title="Application Type"
                      value="Precast Concrete"
                      hasTopBorder={false}
                    />
                    <SideBarEntry title="Precast Cladding" value="Precast Cladding" />
                    <SideBarEntry title="Fixing Context" value="Slab" />
                    <SideBarEntry title="Proof" value="EN 1992-4:2018" />

                    <div className="flex flex-col gap-8 px-5 pt-5 border-t border-gray-100">
                      <div>
                        <InputLabel
                          name="Revision Name"
                          withLine={false}
                        >
                          Revision Name
                        </InputLabel>
                        <TextInput
                          variant="xxs"
                          disabled={generatingReport}
                          onChange={(event) => {
                            setRevisionName(event.target.value)
                            onDetailsChange()
                          }}
                        />
                      </div>

                      <div>
                        <InputLabel
                          name="Design Notes"
                          withLine={false}
                        >
                          Design Notes
                        </InputLabel>
                        <TextBox
                          variant="xxs"
                          disabled={generatingReport}
                          onChange={(event) => {
                            setDesignNotes(event.target.value)
                            onDetailsChange()
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames('w-full flex flex-col items-center bg-gray-20 overflow-y-scroll', {
                      // Not the cleanest, but the background colour stops partway down the page if
                      // this is set when data is loaded
                      'h-full': (!reportUrl && !pageCount) || generatingReport,
                    })}
                  >
                    <div className="flex flex-row justify-between items-center w-[612px] pt-10 pb-[15px]">
                      <h1 className="font-mono uppercase font-semibold text-2xl leading-[28.8px]">Report Preview</h1>
                      <Button
                        size="xs"
                        onClick={handleExport}
                        disabled={generatingReport}
                      >
                        <div className="flex flex-row gap-[3px] justify-center items-center px-5">
                          <ExportIcon />
                          Export Report
                        </div>
                      </Button>
                    </div>
                    {
                      !reportUrl
                        && (
                          <div className="flex items-center justify-center w-full h-full bg-gray-20">
                            <LoadingSpinner className="size-4" />
                          </div>
                        )
                    }
                    {
                      reportUrl
                      && (
                        <Document
                          file={reportUrl}
                          onLoadSuccess={({ numPages }) => setPageCount(numPages)}
                          // Loading component is empty because we're using the LoadingSpinner above
                          loading={<></>}
                        >
                          {pages}
                        </Document>
                      )
                    }
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
